import type { CostBasis } from '../../types/costBasis'
import type { Loan } from '../../types/loan'
import type { BorrowSubCategory, Transaction } from '../../types/transaction'
import { createAssetKey } from '../../utils'
import { mergeActionTransfers } from '../../utils/mergeActionTransfers'
import { createBlankTransaction } from './createBlankTransaction'
import { createBorrowTransaction } from './createBorrowTransaction'
import { createFeeTransaction } from './createFeeTransaction'
import { createTransactionsByTradeAction } from './createTransactionsByTradeAction'
import { replaceBondOfTransfers } from './replaceBondOfTransfers'
import type {
  CreateTransactionsByCategoryActionParameters,
  CreateTransactionsByCategoryActionReturnTypes,
} from './types'
import { createInsufficientTransactionByTransfer } from './util/createInsufficientTransactionByTransfer.ts'

type CreateTransactionsByBorrowActionParameters =
  CreateTransactionsByCategoryActionParameters & {
    readonly costBasis: CostBasis
    readonly handleLoanAsTrade?: boolean
    readonly use5PercentRule?: boolean
    readonly borrowSubCategory?: BorrowSubCategory
  }

type CreateTransactionsByBorrowActionReturnTypes =
  CreateTransactionsByCategoryActionReturnTypes

export const createTransactionsByBorrowAction = ({
  actionId,
  action,
  loans,
  crossActionBundles,
  assetBalances,
  assetBorrows,
  usedBalances,
  nftAcquiredTimestamps,
  costBasis,
  isLocked = false,
  handleLoanAsTrade = false,
  use5PercentRule = false,
  borrowSubCategory,
}: CreateTransactionsByBorrowActionParameters): CreateTransactionsByBorrowActionReturnTypes => {
  // locked action の場合処理しない
  if (isLocked) {
    return {
      transactions: [],
      loans,
      crossActionBundles,
      assetBalances,
      assetBorrows,
      usedBalances,
    }
  }

  if (handleLoanAsTrade) {
    return createTransactionsByTradeAction({
      actionId,
      action,
      loans,
      crossActionBundles,
      assetBalances,
      assetBorrows,
      usedBalances,
      nftAcquiredTimestamps,
      isLocked,
      costBasis,
      use5PercentRule,
    })
  }

  if (action.loanId === undefined) {
    action.loanId = ''
  }

  // merge transfers & replace bond of transfers
  const mergedTransfers = mergeActionTransfers(action.transfers)
  const handleBondResult = replaceBondOfTransfers({
    actionId,
    timestamp: action.timestamp,
    transfers: mergedTransfers,
    loans,
    assetBalances,
    assetBorrows,
    usedBalances,
  })
  const newLoans = handleBondResult.loans
  let newAssetBalances = handleBondResult.assetBalances
  let newAssetBorrows = handleBondResult.assetBorrows
  let newUsedBalances = handleBondResult.usedBalances
  const transactions: Transaction[] = [...handleBondResult.transactions]
  const transfers = handleBondResult.transfers

  if (transfers.length === 0) {
    const result = createBlankTransaction({
      actionId,
      timestamp: action.timestamp,
      assetBalances: newAssetBalances,
      assetBorrows: newAssetBorrows,
      usedBalances: newUsedBalances,
    })
    transactions.push(result.transaction)
    newAssetBalances = result.assetBalances
    newAssetBorrows = result.assetBorrows
    newUsedBalances = result.usedBalances
    return {
      transactions,
      loans: newLoans,
      crossActionBundles,
      assetBalances: newAssetBalances,
      assetBorrows: newAssetBorrows,
      usedBalances: newUsedBalances,
    }
  }

  const loan: Loan = newLoans.get(action.loanId) ?? {
    id: action.loanId,
    borrows: new Map(),
    borrowsWithDebt: new Map(),
    deposits: new Map(),
    depositsWithBond: new Map(),
  }
  for (const transfer of transfers) {
    // in transfer 毎に Loan に borrow data 追加 & borrow transaction 作成
    if (transfer.direction === 'in') {
      const assetKey = createAssetKey(transfer.asset)
      loan.borrows.set(assetKey, [
        ...(loan.borrows.get(assetKey) ?? []),
        {
          asset: assetKey,
          amount: transfer.amount,
        },
      ])

      const result = createBorrowTransaction({
        actionId,
        timestamp: action.timestamp,
        subCategory: borrowSubCategory,
        transfer,
        assetBalances: newAssetBalances,
        assetBorrows: newAssetBorrows,
        usedBalances: newUsedBalances,
      })
      transactions.push(result.transaction)
      newAssetBalances = result.assetBalances
      newAssetBorrows = result.assetBorrows
      newUsedBalances = result.usedBalances
    }

    // out transfer 毎に fee transaction 作成
    if (transfer.direction === 'out') {
      // 不足する場合は insufficient 取引を追加
      const insufficientResult = createInsufficientTransactionByTransfer({
        actionId,
        timestamp: action.timestamp,
        transfer,
        assetBalances: newAssetBalances,
        assetBorrows: newAssetBorrows,
        usedBalances: newUsedBalances,
        costBasis,
      })
      if (insufficientResult !== undefined) {
        transactions.push(insufficientResult.transaction)
        newAssetBalances = insufficientResult.assetBalances
        newAssetBorrows = insufficientResult.assetBorrows
        newUsedBalances = insufficientResult.usedBalances
      }

      // fee transaction 作成
      const result = createFeeTransaction({
        actionId,
        timestamp: action.timestamp,
        subCategory: 'other-fee',
        transfer,
        assetBalances: newAssetBalances,
        assetBorrows: newAssetBorrows,
        usedBalances: newUsedBalances,
        costBasis,
        use5PercentRule,
      })
      transactions.push(result.transaction)
      newAssetBalances = result.assetBalances
      newAssetBorrows = result.assetBorrows
      newUsedBalances = result.usedBalances
    }
  }
  if (
    loan.deposits.size > 0 ||
    loan.depositsWithBond.size > 0 ||
    loan.borrows.size > 0 ||
    loan.borrowsWithDebt.size > 0
  ) {
    newLoans.set(loan.id, loan)
  } else {
    newLoans.delete(loan.id)
  }

  return {
    transactions,
    loans: newLoans,
    crossActionBundles,
    assetBalances: newAssetBalances,
    assetBorrows: newAssetBorrows,
    usedBalances: newUsedBalances,
  }
}
